
import React, { useMemo } from 'react';

// Day mapping for schema.org compliance
const dayMap = {
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
	sunday: 'Sunday',
};

/**
 * Custom hook that generates a SelfStorage schema.org JSON-LD schema.
 * @param {Object} props - Component props containing facility, baseUrl, canonicalUrl, images
 * @returns {{ selfStorageSchema: string }}
 */
export const useSelfStorageSchema = (props) => {
	const {
		facility: facilityProp,
		baseUrl,
		canonicalUrl: canonicalUrlProp,
		images,
	} = props;
	return useMemo(() => {
		const facility = facilityProp || {};
		const canonicalUrl = `${baseUrl || ''}${canonicalUrlProp}`;

		// Helper: normalize time string, including '24 Hour Access'
		const parseTimeString = (timeStr) => {
			if (!timeStr || timeStr === 'Closed') return null;
			if (timeStr === '24 Hour Access') return '00:00-23:59';
			return timeStr;
		};

		// Build opening hours specification from facility.hours.access.text (for customer access hours)
		const buildOpeningHours = () => {
			const hoursText = facility.hours?.access?.text;
			if (!hoursText) return [];

			// Group days with same hours string
			const groupedHours = {};
			Object.entries(hoursText).forEach(([day, hours]) => {
				if (hours && hours !== 'Closed') {
					const normalized = parseTimeString(hours);
					if (normalized) {
						if (!groupedHours[normalized]) {
							groupedHours[normalized] = {
								hours: normalized,
								days: [],
							};
						}
						// Use dayMap only for day names
						if (dayMap[day]) {
							groupedHours[normalized].days.push(dayMap[day]);
						}
					}
				}
			});

			// Convert to OpeningHoursSpecification format
			return Object.values(groupedHours)
				.filter(({ days }) => days && days.length > 0) // Only include if days exist
				.map(({ hours, days }) => {
					if (hours === '00:00-23:59') {
						return {
							'@type': 'OpeningHoursSpecification',
							dayOfWeek: days,
							opens: '00:00',
							closes: '23:59',
						};
					} else if (hours.includes(' - ')) {
						const [opens, closes] = hours.split(' - ');
						return {
							'@type': 'OpeningHoursSpecification',
							dayOfWeek: days,
							opens,
							closes,
						};
					} else {
						return {
							'@type': 'OpeningHoursSpecification',
							dayOfWeek: days,
							opens: hours,
							closes: hours,
						};
					}
				});
		};

		const openingHours = buildOpeningHours();

		const amenityFeatures = Array.isArray(facility?.collectAmenities)
			? facility.collectAmenities
			: [];

		// Extract payment methods where hasAttribute is true
		const paymentMethodsMap = {
			cashAccepted: 'Cash',
			creditCardsAccepted: 'Credit Card',
			checksAccepted: 'Check',
		};

		const paymentMethods =
			facility?.featuresDataByCategory?.payments?.items
				?.filter((item) => item.hasAttribute === true)
				.map((item) => paymentMethodsMap[item.key])
				.filter(Boolean) || [];

		const selfStorageSchema = {
			'@context': 'https://schema.org',
			'@type': 'SelfStorage',
			'@id': `${canonicalUrl}#selfstorage`,
			name: facility?.name,
			url: canonicalUrl,
			telephone: facility?.phone,
			priceRange: '$$',
			paymentAccepted: paymentMethods,
			address: {
				'@type': 'PostalAddress',
				streetAddress: facility?.location?.streetAddress,
				addressLocality: facility?.location?.city,
				addressRegion: facility?.location?.state,
				postalCode: facility?.location?.zip,
				addressCountry: facility?.location?.country || 'US',
			},
			geo: {
				'@type': 'GeoCoordinates',
				latitude: facility?.location?.latitude,
				longitude: facility?.location?.longitude,
			},
			hasMap: `https://www.google.com/maps?q=${facility?.location?.latitude},${facility?.location?.longitude}`,
			image:
				images
					?.filter((img) => img?.src)
					.map((img) => `https:${img.src}`) || [],
		};

		// Add opening hours if available
		if (openingHours.length > 0) {
			selfStorageSchema.openingHoursSpecification = openingHours;
		}

		// Add amenity features if available
		if (amenityFeatures.length > 0) {
			selfStorageSchema.amenityFeature = amenityFeatures;
		}

		// Add aggregate rating if valid data exists
		if (
			facility?.reviewMetadata?.average &&
			facility?.reviewMetadata?.count
		) {
			selfStorageSchema.aggregateRating = {
				'@type': 'AggregateRating',
				ratingValue: facility.reviewMetadata.average,
				ratingCount: facility.reviewMetadata.count,
			};
		}

		// Add Sparefoot reviews if available
		if (Array.isArray(facility?.reviews) && facility.reviews.length > 0) {
			selfStorageSchema.review = facility.reviews
				.filter((review) => review?.nickname && review?.rating)
				.map((review) => ({
					'@type': 'Review',
					author: {
						'@type': 'Person',
						name: review.nickname,
					},
					reviewRating: {
						'@type': 'Rating',
						ratingValue: review.rating,
					},
					reviewBody: review.message,
					publisher: {
						'@type': 'Organization',
						name: 'SpareFoot',
					},
				}));
		}

		// Add Google reviews if available
		if (
			facility?.googleReviews?.reviews &&
			Array.isArray(facility.googleReviews.reviews) &&
			facility.googleReviews.reviews.length > 0
		) {
			const googleReviewsSchema = facility.googleReviews.reviews
				.filter((review) => review?.authorName && review?.rating)
				.map((review) => ({
					'@type': 'Review',
					author: {
						'@type': 'Person',
						name: review.authorName,
					},
					reviewRating: {
						'@type': 'Rating',
						ratingValue: review.rating,
					},
					reviewBody: review.text,
					publisher: {
						'@type': 'Organization',
						name: 'Google',
					},
				}));

			// Combine with existing reviews or create new review array
			selfStorageSchema.review = [
				...(selfStorageSchema.review || []),
				...googleReviewsSchema,
			];
		}

		const stringifiedSelfStorageSchema = JSON.stringify(selfStorageSchema);

		return {
			selfStorageSchema: stringifiedSelfStorageSchema,
		};
	}, [facilityProp, baseUrl, canonicalUrlProp, images]);
};

function withSelfStorage(WrappedComponent) {
	const WithSelfStorage = (props) => {
		const hookResult = useSelfStorageSchema(props);
		return (
			<WrappedComponent
				{...props}
				{...hookResult}
			/>
		);
	};
	WithSelfStorage.displayName = `withSelfStorage(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithSelfStorage;
}

export default withSelfStorage;
